import { ResourceImage } from "./ResourceImage"
import { FieldData } from "../../../../-components/field-data"
import { IconCheckBox } from "../../-components/icon-check-box"
import { useTranslation } from "react-i18next"
import { getFileType } from "@/utils/getFileType"
import {
  FieldType,
  ResourceObjectDto,
  ResourceObjectDtoPagination,
} from "@/orval/gen"
import {
  InfiniteScrollingTable,
  SortingStateAndUpdater,
  TheasoftColumnDef,
} from "@/routes/-components/custom-tanstack-table"
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query"

interface Props {
  fieldTypes: FieldType[]
  queryResult: UseInfiniteQueryResult<
    InfiniteData<ResourceObjectDtoPagination, unknown>,
    Error
  >
  sortingState: SortingStateAndUpdater
  onClickItem?: (item: ResourceObjectDto) => void
}

export function ResourceObjectsTable({
  fieldTypes,
  queryResult,
  sortingState,
  onClickItem,
}: Props) {
  const { t } = useTranslation("app_server")

  const columns = React.useMemo<TheasoftColumnDef<ResourceObjectDto>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("Name"),
      },
      {
        accessorKey: "identificationCode",
        header: t("InventoryNumber"),
      },
      {
        accessorKey: "type",
        header: t("Type"),
      },
      {
        accessorKey: "condition",
        header: t("Condition"),
      },
      {
        accessorKey: "available",
        header: t("Available"),
        cell: ({ row }) => <IconCheckBox checked={row.original.available} />,
        enableSorting: false,
      },
      {
        accessorKey: "attachmentId",
        header: t("Picture"),
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const image = row.original.attachments.find(
            (a) => getFileType(a.fileName) === "image"
          )
          if (!image) return null
          return (
            <ResourceImage
              attachmentId={image.attachmentId}
              resourceId={row.original.resourceId}
              h={"100%"}
              w={"100%"}
              mah={100}
            />
          )
        },
      },
      ...fieldTypes.map<TheasoftColumnDef<ResourceObjectDto>>((type) => ({
        header: type.name,
        accessorKey: type.name,
        enableSorting: false,
        cell: (cell) => (
          <FieldData
            fieldData={cell.row.original.resourceFields.find(
              (field) => field.type.name == type.name
            )}
          />
        ),
      })),
    ],
    [t, fieldTypes]
  )
  return (
    <InfiniteScrollingTable<ResourceObjectDto>
      id="resource-objects-table"
      infiniteQueryResult={queryResult}
      columnDefinitions={columns}
      sortingState={sortingState}
      getRowProps={(row) => ({
        onClick: () => onClickItem?.(row.original),
      })}
    />
  )
}
